// src/pages/RiskDisclosurePage.jsx
import React from 'react';
import { Container, Typography, Box, Paper, Alert, List, ListItem, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Warning } from '@mui/icons-material';

const RiskDisclosurePage = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Box sx={{ py: 8, minHeight: '100vh' }}>
      <Container maxWidth="md">
        <Paper 
          elevation={3}
          sx={{
            p: 4,
            background: isDarkMode 
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'none',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`
          }}
        >
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, color: '#5980a6' }}>
            Risk Disclosure Statement
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary' }}>
            Last updated: {new Date().toLocaleDateString()}
          </Typography>

          <Alert 
            severity="warning" 
            icon={<Warning />}
            sx={{ mb: 4, fontWeight: 600 }}
          >
            TRADING INVOLVES SUBSTANTIAL RISK OF LOSS AND IS NOT SUITABLE FOR ALL INVESTORS. 
            YOU SHOULD CAREFULLY CONSIDER WHETHER TRADING IS SUITABLE FOR YOU IN LIGHT OF YOUR CIRCUMSTANCES, 
            KNOWLEDGE, AND FINANCIAL RESOURCES.
          </Alert>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Important Risk Warning
            </Typography>
            <Typography variant="body1" paragraph>
              XAUPilot provides trading signals and market analysis for educational and informational purposes only. 
              Our content is not intended as investment advice, and you should not rely solely on our signals 
              when making trading decisions. All trading involves risk, and past performance does not guarantee future results.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Specific Risks of Precious Metals Trading
            </Typography>
            <Typography variant="body1" paragraph>
              Trading in precious metals, including gold (XAU), involves specific risks that you should understand:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Price Volatility"
                  secondary="Precious metals prices can fluctuate rapidly and substantially due to various factors including economic conditions, geopolitical events, and market sentiment"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Leverage Risk"
                  secondary="Many precious metals trading instruments involve leverage, which can amplify both profits and losses. Small price movements can result in significant gains or losses"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Market Hours"
                  secondary="Precious metals markets operate in different time zones and may have gaps in pricing, leading to potential slippage between expected and actual execution prices"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Liquidity Risk"
                  secondary="During certain market conditions, it may be difficult to execute trades at desired prices due to reduced market liquidity"
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Signal Performance Disclaimer
            </Typography>
            <Typography variant="body1" paragraph>
              While we strive to provide accurate and timely trading signals, you should be aware that:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="No signal service can guarantee profitable trades" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Past performance of our signals does not predict future results" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Market conditions can change rapidly, affecting signal effectiveness" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Technical difficulties may cause delays in signal delivery" />
              </ListItem>
              <ListItem>
                <ListItemText primary="You may experience periods of consecutive losing trades" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Risk Management Guidelines
            </Typography>
            <Typography variant="body1" paragraph>
              To help manage your trading risks, we strongly recommend:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Risk Only What You Can Afford to Lose"
                  secondary="Never trade with money needed for essential expenses like housing, food, or medical care"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Use Proper Position Sizing"
                  secondary="Limit individual trade sizes to a small percentage of your total trading capital (typically 1-2%)"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Set Stop-Loss Orders"
                  secondary="Always use stop-loss orders to limit potential losses on each trade"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Diversify Your Trading"
                  secondary="Don't put all your capital into a single trade or market"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Maintain Trading Records"
                  secondary="Keep detailed records of all trades to analyze your performance and identify areas for improvement"
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Regulatory Information
            </Typography>
            <Typography variant="body1" paragraph>
              XAUPilot is an information service and does not provide investment advice or manage client funds. 
              We are not registered as an investment advisor or broker-dealer. Our signals and analysis are 
              provided for educational purposes only and should not be considered as personalized investment advice.
            </Typography>
            <Typography variant="body1" paragraph>
              If you are unsure about the suitability of trading or need personalized advice, please consult 
              with a qualified financial advisor who can assess your individual circumstances.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Technology and Operational Risks
            </Typography>
            <Typography variant="body1" paragraph>
              Trading signals and market access depend on technology systems that may experience:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Internet connectivity issues" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Platform downtime or technical failures" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Delays in signal transmission" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Data feed interruptions" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Cybersecurity threats" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              No Guarantee of Performance
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 600, color: '#cf6f61' }}>
              THERE IS NO GUARANTEE THAT YOU WILL MAKE MONEY USING OUR SIGNALS OR FOLLOWING OUR ANALYSIS. 
              TRADING RESULTS VARY WIDELY AND DEPEND ON MANY FACTORS INCLUDING MARKET CONDITIONS, 
              EXECUTION TIMING, POSITION SIZING, AND INDIVIDUAL TRADING DECISIONS.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Acknowledgment
            </Typography>
            <Typography variant="body1" paragraph>
              By using XAUPilot's services, you acknowledge that you have read, understood, and agree to this 
              risk disclosure statement. You understand the risks involved in trading and accept full 
              responsibility for any trading decisions you make.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Questions?
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions about these risks or need clarification on any aspect of our service, 
              please contact us at support@XAUPilot.com before beginning to trade.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default RiskDisclosurePage;